import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import StockItemInfo from '../components/StockItemInfo'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'

function AmazonEconomicsDetailScreen() {
    const { queryId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [query, setQuery] = useState(null)
    const [queryLoading, setQueryLoading] = useState(true)
    const [error, setError] = useState('')

    const [economicsData, setEconomicsData] = useState([])
    const [economicsSummary, setEconomicsSummary] = useState(null)
    const [economicsLoading, setEconomicsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [exporting, setExporting] = useState(false)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const initialTableState = {
        visibility: {
            image: true, description: true, sku: true, asin: true,
            unitsSold: true, unitsReturned: true, revenue: true,
            refundedSales: true, netSales: true, avgSellingPrice: true,
            netUnitsSold: true, fbaFees: true, referralFees: true,
            otherFees: true, cogs: true, cogsPerUnit: true,
            netProceedsPerUnit: true, netProfit: true, margin: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_economics', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        image: 'Image', description: 'Description', sku: 'SKU', asin: 'ASIN',
        unitsSold: 'Units Sold', unitsReturned: 'Units Returned', revenue: 'Revenue',
        refundedSales: 'Refunded Sales', netSales: 'Net Sales', avgSellingPrice: 'Avg Selling Price',
        netUnitsSold: 'Net Units Sold', fbaFees: 'FBA Fees', referralFees: 'Referral Fees',
        otherFees: 'Other Fees', cogs: 'COGS', cogsPerUnit: 'COGS/Unit',
        netProceedsPerUnit: 'Net Proceeds/Unit', netProfit: 'Net Profit', margin: 'Margin %',
    }

    useEffect(() => {
        const fetchQuery = async () => {
            setQueryLoading(true)
            try {
                const data = await amazonAPI.queries.getById(queryId)
                setQuery(data)
            } catch (err) {
                setError(formatErrorResponse(err))
            } finally {
                setQueryLoading(false)
            }
        }
        fetchQuery()
    }, [queryId])

    useEffect(() => {
        if (!query) return
        const fetchSummary = async () => {
            try {
                const summary = await amazonAPI.economics.getSummary({
                    account_id: query.seller_account,
                    period_start: query.data_start_date,
                    period_end: query.data_end_date,
                })
                setEconomicsSummary(summary || null)
            } catch (err) {
                console.error('Failed to fetch economics summary:', err)
            }
        }
        fetchSummary()
    }, [query])

    const fetchEconomicsData = useCallback(async () => {
        if (!query) return
        setEconomicsLoading(true)
        setError('')
        try {
            const params = {
                account_id: query.seller_account,
                period_start: query.data_start_date,
                period_end: query.data_end_date,
                page: currentPage,
                pageSize: pageSize,
            }
            if (searchTerm) params.searchTerm = searchTerm
            if (sortColumn) {
                params.sortColumn = sortColumn
                params.sortDirection = sortDirection || 'asc'
            }

            const data = await amazonAPI.economics.getAll(params)
            if (data && data.results !== undefined) {
                setEconomicsData(data.results)
                setTotalItems(data.count)
            } else {
                setEconomicsData(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setEconomicsLoading(false) }
    }, [query, currentPage, pageSize, searchTerm, sortColumn, sortDirection])

    useEffect(() => { fetchEconomicsData() }, [fetchEconomicsData])

    const handleExport = async (format) => {
        if (!query) return
        setExporting(true)
        setError('')
        try {
            await amazonAPI.economics.export({ account_id: query.seller_account, period_start: query.data_start_date, period_end: query.data_end_date }, format)
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setExporting(false) }
    }

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1) }, 300)
    }
    const handleClearSearch = () => { setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1) }

    const handleSort = (column, direction) => { setSortColumn(column); setSortDirection(direction); setCurrentPage(1) }

    const economicsColumns = [
        columnVisibility.image && { id: 'image', header: 'Image', cell: (row) => row.stock_item_detail?.image ? (
            <OverlayTrigger placement="right" overlay={<Tooltip className="stock-item-tooltip"><StockItemInfo stockItem={row.stock_item_detail} /></Tooltip>}>
                <img src={row.stock_item_detail.image} alt="Stock Item Thumbnail" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </OverlayTrigger>
        ) : null },
        columnVisibility.description && { id: 'description', header: 'Description', sortable: true, cell: (row) => row.stock_item_detail?.description || '' },
        columnVisibility.sku && { id: 'sku', header: 'SKU', cell: (row) => row.amazon_sku },
        columnVisibility.asin && { id: 'asin', header: 'ASIN', cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        columnVisibility.unitsSold && { id: 'unitsSold', header: 'Units Sold', sortable: true, cell: (row) => row.units_sold },
        columnVisibility.unitsReturned && { id: 'unitsReturned', header: 'Units Returned', sortable: true, cell: (row) => row.units_returned },
        columnVisibility.revenue && { id: 'revenue', header: 'Revenue', sortable: true, cell: (row) => formatCurrency(row.revenue) },
        columnVisibility.refundedSales && { id: 'refundedSales', header: 'Refunded Sales', sortable: true, cell: (row) => row.refunded_sales != null ? formatCurrency(row.refunded_sales) : '' },
        columnVisibility.netSales && { id: 'netSales', header: 'Net Sales', sortable: true, cell: (row) => row.net_sales != null ? formatCurrency(row.net_sales) : '' },
        columnVisibility.avgSellingPrice && { id: 'avgSellingPrice', header: 'Avg Selling Price', sortable: true, cell: (row) => row.avg_selling_price != null ? formatCurrency(row.avg_selling_price) : '' },
        columnVisibility.netUnitsSold && { id: 'netUnitsSold', header: 'Net Units Sold', sortable: true, cell: (row) => row.net_units_sold != null ? row.net_units_sold : '' },
        columnVisibility.fbaFees && { id: 'fbaFees', header: 'FBA Fees', sortable: true, cell: (row) => formatCurrency(row.fba_fees) },
        columnVisibility.referralFees && { id: 'referralFees', header: 'Referral Fees', sortable: true, cell: (row) => formatCurrency(row.referral_fees) },
        columnVisibility.otherFees && { id: 'otherFees', header: 'Other Fees', sortable: true, cell: (row) => formatCurrency(row.other_fees) },
        columnVisibility.cogs && { id: 'cogs', header: 'COGS', sortable: true, cell: (row) => formatCurrency(row.cogs) },
        columnVisibility.cogsPerUnit && { id: 'cogsPerUnit', header: 'COGS/Unit', sortable: true, cell: (row) => row.units_sold > 0 ? formatCurrency(Number(row.cogs) / row.units_sold) : '' },
        columnVisibility.netProceedsPerUnit && { id: 'netProceedsPerUnit', header: 'Net Proceeds/Unit', sortable: true, cell: (row) => row.net_proceeds_per_unit != null ? formatCurrency(row.net_proceeds_per_unit) : '' },
        columnVisibility.netProfit && { id: 'netProfit', header: 'Net Profit', sortable: true, cell: (row) => formatCurrency(row.net_profit) },
        columnVisibility.margin && { id: 'margin', header: 'Margin %', sortable: true, cell: (row) => row.margin_percent != null ? `${Number(row.margin_percent).toFixed(1)}%` : '' },
    ].filter(Boolean)

    if (queryLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = query
        ? `${query.seller_account_name || ''} (${formatDateOnly(query.data_start_date)} – ${formatDateOnly(query.data_end_date)})`
        : ''

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">SKU Economics</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-economics', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {economicsLoading && economicsData.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    {economicsSummary && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Total Revenue</h5><h3 className="mb-0">{formatCurrency(economicsSummary.total_revenue)}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total Units Sold</h5><h3 className="mb-0">{economicsSummary.total_units_sold ?? 0}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Net Profit</h5><h3 className="mb-0">{formatCurrency(economicsSummary.total_net_profit)}</h3></div></Col>
                            <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Overall Margin</h5><h3 className="mb-0">{economicsSummary.overall_margin != null ? `${Number(economicsSummary.overall_margin).toFixed(1)}%` : ''}</h3></div></Col>
                            <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">SKU Count</h5><h3 className="mb-0">{economicsSummary.sku_count ?? 0}</h3></div></Col>
                            <Col><div className="bg-danger text-white p-3 rounded text-center"><h5 className="mb-1">TACoS</h5><h3 className="mb-0">{economicsSummary.tacos != null && Number(economicsSummary.tacos) > 0 ? `${Number(economicsSummary.tacos).toFixed(1)}%` : ''}</h3></div></Col>
                        </Row>
                    )}
                    <div className="d-flex align-items-center mb-3">
                        <Button variant="success" onClick={() => handleExport('csv')} disabled={exporting} className="me-2">
                            <i className="bi bi-file-earmark-spreadsheet me-2"></i>Export CSV
                        </Button>
                        <Button variant="info" onClick={() => handleExport('excel')} disabled={exporting} className="me-2">
                            <i className="bi bi-file-earmark-excel me-2"></i>Export Excel
                        </Button>
                        <div className="ms-2">{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch} onClear={handleClearSearch} placeholder="Search SKU or ASIN..." />
                        </div>
                    </div>
                    {economicsData.length === 0 ? (
                        <p className="text-muted">No economics data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={economicsColumns} data={economicsData} tableId="amazon_economics"
                                    enableSorting={true} onSort={handleSort} sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        sku: 150, asin: 130, unitsSold: 100, unitsReturned: 120,
                                        revenue: 120, refundedSales: 120, netSales: 120, avgSellingPrice: 130,
                                        netUnitsSold: 110, fbaFees: 110, referralFees: 120, otherFees: 110,
                                        cogs: 110, cogsPerUnit: 110, netProceedsPerUnit: 130, netProfit: 120, margin: 100
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonEconomicsDetailScreen
